/**
 * 受け口が URL から案件番号を読む口。業務マップ・業務詳細・案件の変更一覧・ヘッダーが
 * 同じ関門で読むために、どの機能にも属さない場所に置く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件とパスパラメータの綴りを、契約と同じ関門で案件番号の数へ読む。
 * @graph-connects parsePjSearch [calls] 検索条件から案件番号を読む
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 */
import { useRouterState } from '@tanstack/react-router';

import { parsePjSearch, pjTextOf } from '../pj-search.js';

/**
 * 検索条件の案件番号を、作り直しの読取が受ける数へ読む。読めなければ `null`。
 *
 * **綴りが 2 通りある。** 作り直しの要素表の案件番号は数（`139162`）だが、旧表を読む画面が
 * 使う `?pj=` は接頭辞つきの綴り（`AIRCLOSET-139162`）である。切替の途中は同じ `?pj=` の
 * 鍵に両方が載る — サイドバー（`renderNavLink`）も業務詳細の戻るリンクも、いまの現在地に
 * 載っている綴りをそのまま次の画面へ写すからである。片方しか読まないと、旧表の画面から
 * 業務マップへ移った瞬間に案件が読めなくなり、**案内（`PjModeBadge`）だけが案件モードを
 * 名乗って中身はマスター**という食い違いが起きる。両方受けてこの食い違いを作らせない。
 *
 * 切替が終わって `?pj=` の綴りが数だけになったら、接頭辞を落とす分岐ごと消す。
 *
 * @param raw 検索条件の案件番号。指定が無ければ undefined
 * @returns 案件番号。1 以上の整数として読めなければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件の案件番号を、数の綴りと接頭辞つきの綴りの両方から読む。
 * @graph-connects readPjNumberParam [calls] 綴りを契約と同じ関門で案件番号へ読む
 */
export function readPjSearchNumber(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const digits = /^(?:AIRCLOSET-)?(\d+)$/.exec(raw.trim())?.[1];
  return digits === undefined ? null : readPjNumberParam(digits);
}

/**
 * 現在地の検索条件から案件番号を読む。
 *
 * 読み方に検証器（`parsePjSearch`）をそのまま使うのは、**サイドバーが案件モードへ切り替わる
 * 条件と同じ条件で読むため**である。受け口ごとに書き下すと、空文字（`?pj=`）の畳み方
 * ひとつで「切り替わるのに読めない」隙間が受け口の数だけ作れる。
 *
 * @returns 案件番号。読めなければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 現在地の検索条件から、サイドバーと同じ判定で案件番号を読む。
 * @graph-connects parsePjSearch [calls] 検索条件から案件番号を読む
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 * @graph-connects readPjSearchNumber [calls] 綴りを案件番号の数へ読む
 */
export function usePjSearchNumber(): number | null {
  const pj = useRouterState({
    select: (state) => pjTextOf(parsePjSearch(state.location.search as Record<string, unknown>).pj),
  });

  return readPjSearchNumber(pj);
}

/**
 * URL のパスから案件番号を読む。1 以上の整数でなければ `null` を返す。
 *
 * 関門を「1 以上の整数」に置くのは契約（`ElementPjNumberSchema`）と同じ線である（決定 2）。
 * `Number()` は空文字を 0 に、末尾の空白を無視して数に変えるので、綴りをそのまま確かめる。
 * ここで弾かないと、読取が入力の検証で失敗した姿が「取得に失敗しました」として出て、
 * URL の打ち間違いと本当の失敗が利用者に区別できなくなる。
 *
 * @param raw パスパラメータの綴り
 * @returns 案件番号。1 以上の整数でなければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business パスパラメータの綴りを、契約と同じ関門（1 以上の整数）で案件番号へ読む。
 * @graph-connects none
 */
export function readPjNumberParam(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return parsed >= 1 ? parsed : null;
}
